import { forwardRef, useEffect } from "react";
import { pushBusyCursor, popBusyCursor } from "../../../_utils/busyCursor";
import "./_button.scss";

/**
 * Pure-UI button. No API calls, no Redux, no routing, no tooltip logic -
 * consumers compose behavior (and, if needed, a tooltip via
 * `_components/tooltip/Tooltip`) around this rather than this component
 * reaching out for it.
 *
 * `icon` renders before `children` (pass an already-sized icon element,
 * e.g. `<Plus size={14} />`) - covers icon+text ("+ Add item") and,
 * combined with `iconOnly` (tightens padding toward square) and no
 * `children`, icon-only buttons (a row's remove control).
 *
 * Variants: `contained` (solid primary CTA) / `contained-danger` (solid
 * destructive CTA - a labeled "Discard", not the icon-only remove control
 * below) / `outlined` (the workhorse secondary - a neutral glass pill per
 * the Liquid Glass design, no longer accent-bordered) / `outlined-neutral`
 * (legacy alias, renders identically to `outlined` since the glass restyle)
 * / `text` (borderless, small horizontal padding)
 * / `ghost` (borderless, no padding, no hover box - inline text/icon
 * actions) / `ghost-danger` (ghost, always red - icon-only remove/delete
 * only) / `tab` (underlined segment control - pair with the `active` prop
 * to mark the selected tab; has no bg/border of its own). See _button.scss
 * for what distinguishes each.
 *
 * `loadingText`: swaps the button's own label for this while `loading` -
 * e.g. `<Button loading={isSubmitting}>Create account</Button>` shows
 * "Creating account…" without passing anything extra, via a built-in
 * verb->gerund lookup on the label's first word (see GERUND_MAP below).
 * Pass `loadingText` explicitly to override that guess, or when the label's
 * first word isn't a recognized verb.
 */
// First-word verb -> gerund, for the common "Create"/"Save"/"Delete this
// row" style button labels used across the app's ~40 modals. Deliberately a
// fixed lookup, not a generated (regex/suffix-stripping) transform - English
// gerund spelling has enough irregulars (Cancel -> Cancelling, Submit ->
// Submitting, double-consonant rules, etc.) that a generated rule would get
// some of these wrong; a small table only ever produces text that's exactly
// right. Unknown first words fall back to the original label unchanged (see
// `deriveLoadingText`) - the spinner alone still communicates "busy", never
// wrong wording.
const GERUND_MAP = {
  create: "Creating",
  save: "Saving",
  add: "Adding",
  update: "Updating",
  delete: "Deleting",
  remove: "Removing",
  send: "Sending",
  invite: "Inviting",
  export: "Exporting",
  import: "Importing",
  upload: "Uploading",
  download: "Downloading",
  suspend: "Suspending",
  activate: "Activating",
  reactivate: "Reactivating",
  cancel: "Cancelling",
  confirm: "Confirming",
  submit: "Submitting",
  approve: "Approving",
  reject: "Rejecting",
  publish: "Publishing",
  archive: "Archiving",
  unarchive: "Unarchiving",
  restore: "Restoring",
  generate: "Generating",
  duplicate: "Duplicating",
  apply: "Applying",
  assign: "Assigning",
  reset: "Resetting",
  connect: "Connecting",
  disconnect: "Disconnecting",
  verify: "Verifying",
  retry: "Retrying",
  validate: "Validating",
  revalidate: "Revalidating",
};

// Only string children are safe to inspect (an icon+text node tree has no
// single "first word") - anything else just keeps its own children while
// loading, same as before this prop existed.
const deriveLoadingText = (children) => {
  if (typeof children !== "string") return children;
  const [firstWord, ...rest] = children.split(" ");
  const gerund = GERUND_MAP[firstWord.toLowerCase()];
  if (!gerund) return children;
  return [gerund, ...rest].join(" ") + "…";
};

const Button = forwardRef(function Button(
  {
    variant = "contained",
    size = "md",
    loading = false,
    loadingText,
    disabled = false,
    fullWidth = false,
    iconOnly = false,
    active = false,
    icon,
    type = "button",
    className = "",
    children,
    ...rest
  },
  ref,
) {
  // Page-level "the app is busy" signal (cursor: wait - see
  // _styles/base/_busy-cursor.scss) for as long as this button is loading,
  // not just the button's own spinner - a "Creating…" button was
  // previously the only visible sign anything was happening; the rest of
  // the page, including the cursor, still read as idle.
  useEffect(() => {
    if (!loading) return;
    pushBusyCursor();
    return () => popBusyCursor();
  }, [loading]);

  const classes = [
    "button",
    `button--${variant}`,
    `button--${size}`,
    fullWidth ? "button--full-width" : "",
    loading ? "button--loading" : "",
    iconOnly ? "button--icon-only" : "",
    active ? "button--active" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="button__spinner" aria-hidden="true" />}
      <span className="button__content">
        {icon}
        {loading ? (loadingText ?? deriveLoadingText(children)) : children}
      </span>
    </button>
  );
});

export default Button;
